import type { ThemeColorName } from '@examdojo/styles';
import type { Board, GeometryElement, Text } from 'jsxgraph';
import type { JsxgraphThemeService } from '../jsxgraph-theme.service';
import type { ElementLabelConfig, LabelVariant } from './label/label.model';
import { getLabelStyle } from './label/label.utils';

/**
 * Extended Text interface with updateText method.
 * The method exists at runtime but is missing from JSXGraph's type definitions.
 */
export interface TextElement extends Text {
  updateText(): void;
}

export interface LabelCreatorOptions {
  board: Board;
  container: HTMLElement;
  themeService: JsxgraphThemeService;
  point: GeometryElement;
  labelText: string | (() => string);
  config: ElementLabelConfig;
  offset?: [number, number];
}

/**
 * Convert pixel offset to coordinate offset based on current board zoom level.
 * This ensures labels maintain consistent visual spacing regardless of zoom.
 */
export function pixelsToCoords(board: Board, container: HTMLElement, pixels: number, axis: 'x' | 'y'): number {
  const bbox = board.getBoundingBox();
  const containerRect = container.getBoundingClientRect();

  if (axis === 'x') {
    const coordWidth = bbox[2] - bbox[0];
    return pixels * (coordWidth / containerRect.width);
  } else {
    const coordHeight = bbox[1] - bbox[3];
    return pixels * (coordHeight / containerRect.height);
  }
}

export function createLabel(opts: LabelCreatorOptions): GeometryElement {
  const { board, container, themeService, point, labelText, config, offset } = opts;

  const variant: LabelVariant = config.variant ?? 'secondary';
  const color: ThemeColorName = config.color ?? 'neutral';

  const theme = themeService.getTheme();
  const labelStyle = getLabelStyle(themeService, variant, color);
  const hasBackground = variant !== 'tertiary';
  const labelOffset = offset ?? theme.spacing.labelOffset;

  // Support both static strings and dynamic functions for label text
  const getText = typeof labelText === 'function' ? labelText : () => labelText;

  const typedPoint = point as unknown as { X: () => number; Y: () => number };
  const label = board.create(
    'text',
    [
      () => typedPoint.X() + pixelsToCoords(board, container, labelOffset[0], 'x'),
      () => typedPoint.Y() + pixelsToCoords(board, container, labelOffset[1], 'y'),
      () => `<span style="
        ${hasBackground ? `background-color: ${labelStyle.backgroundColor};` : ''}
        color: ${labelStyle.textColor};
        ${hasBackground ? `padding: ${theme.spacing.labelPadding};` : ''}
        ${hasBackground ? 'border-radius: 6px;' : ''}
        font-size: ${theme.typography.fontSize.label}px;
        font-weight: ${theme.typography.fontWeight.bold};
        font-family: ${theme.typography.fontFamily};
        white-space: nowrap;
        ${hasBackground ? 'display: inline-block;' : ''}
      ">${getText()}</span>`,
    ],
    {
      anchorX: 'left',
      anchorY: 'middle',
      parse: false,
      useMathjax: false,
      useMathJax: false,
      cssClass: `jsx-${variant}-label-html`,
    },
  );

  return label as GeometryElement;
}

/**
 * Force text labels to re-evaluate their content functions.
 * Call this after board.update() when parameters affecting label text change.
 *
 * Labels are identified by keys ending with '-label' in the objects map.
 */
export function updateLabels(objects: Map<string, GeometryElement>): void {
  [...objects.entries()]
    .filter((entry): entry is [string, TextElement] => entry[0].endsWith('-label'))
    .forEach(([, obj]) => obj.updateText());
}
